// Airport service — search and detail. Pure server-side.
//
// IMPORTANT: this module previously cached the entire 32k-airport dataset
// in memory (and IndexedDB) on the client. That was crashing iOS Safari
// (~80MB heap from one parsed array). It now hits server endpoints for
// every search/detail; a viewport-bbox slice for the map comes from
// /api/bbox via src/lib/mapBbox.ts.

import { searchAny } from './search';

export type AirportType =
  | 'large_airport' | 'medium_airport' | 'small_airport'
  | 'heliport' | 'seaplane_base' | 'closed';

export interface Airport {
  id?: string;
  ident: string;
  type: AirportType | string;
  name: string;
  lat: number;
  lon: number;
  elev?: number | null;
  iso_country?: string;
  iso_region?: string;
  city?: string;
  scheduled?: boolean;
  icao?: string;
  iata?: string;
  gps?: string;
  local?: string;
  /** NASR-derived. Optional. Surfaced by /api/airport for US fields. */
  fuelTypes?: string[];
  hasTower?: boolean;
  ownership?: string;
  facilityUse?: string;
  lightingSchedule?: string;
  magVar?: number | null;
}

export interface Runway {
  airport_ident: string;
  length_ft: number | null;
  width_ft: number | null;
  surface: string;
  lighted: boolean;
  closed: boolean;
  le_ident: string; he_ident: string;
  le_heading: number | null; he_heading: number | null;
}

export interface Frequency {
  airport_ident: string;
  type: string;
  description: string;
  frequency_mhz: string;
}

/**
 * Field-operations extras returned by /api/airport. Optional fields
 * are absent when NASR doesn't publish a value for that airport, and
 * the whole `ops` object is absent for non-US airports (OurAirports
 * fallback). UI renders structured sub-sections only when something
 * is present; everything else is covered by the subscription-required
 * stub at the bottom of the Info tab.
 */
export interface AirportOps {
  manager?: { name?: string; cityStateZip?: string; phone?: string };
  owner?:   { name?: string; cityStateZip?: string; phone?: string };
  beaconLightingSchedule?: string;
  beaconColor?: string;
  windIndicator?: string;
  segmentedCircle?: string;
  contractFuel?: boolean;
  airframeRepair?: string;
  engineRepair?: string;
  bottledOxygen?: string;
  bulkOxygen?: string;
  transientStorage?: string[];
  otherServices?: string[];
  arffCertification?: string;
  npiasFedAgreementCodes?: string;
  customsAirportOfEntry?: boolean;
  customsLandingRights?: boolean;
  jointUseCivilAtMilitary?: boolean;
  militaryLandingRights?: boolean;
  landingFeeNonCommercial?: boolean;
  medicalUse?: boolean;
  activationDate?: string;
  sectionalChart?: string;
  trafficPatternAltitudeAgl?: number | null;
  attendance?: string[];
  remarks?: Array<{ key: string; text: string }>;
}

export interface AirportDetail {
  version: string;
  airport: Airport;
  runways: Runway[];
  frequencies: Frequency[];
  ops?: AirportOps;
}

const DETAIL_API = '/api/airport?ident=';

/** Server-side typeahead search for airports. */
export async function searchAirports(query: string, limit = 30): Promise<Airport[]> {
  const norm = query.trim();
  // Empty query → return some defaults via the server hits empty-string check
  // (server returns 400 on empty). Just return [] to keep callers happy.
  if (!norm) return [];
  try {
    const hits = await searchAny(norm, { kind: 'airport', limit });
    return hits.map((h) => ({
      ident: h.ident,
      type: 'unknown' as AirportType,
      name: h.name,
      lat: h.lat,
      lon: h.lon,
      city: h.sub,
    }));
  } catch {
    return [];
  }
}

/** Get an airport by ident (calls /api/airport which has full detail incl. runways/freqs). */
export async function getAirport(ident: string): Promise<Airport | null> {
  const detail = await getAirportDetail(ident);
  return detail?.airport ?? null;
}

/** Full airport detail (runways + frequencies) from the server endpoint. */
export async function getAirportDetail(ident: string): Promise<AirportDetail | null> {
  const norm = ident.trim().toUpperCase();
  if (!norm) return null;
  const r = await fetch(DETAIL_API + encodeURIComponent(norm));
  if (r.status === 404) return null;
  if (!r.ok) throw new Error('airport detail fetch failed: ' + r.status);
  return (await r.json()) as AirportDetail;
}
